/* global flatpickr */
(function () {
  'use strict';

  const selector = 'input[type="date"].rs-input-type__date:not(.flatpickr-input)';

  // Close any open flatpickr calendar when the user scrolls or wheels the page.
  function _scrollCloseHandler(e) {
    const openCal = document.querySelector('.flatpickr-calendar.open');
    if (!openCal) return;
    if (openCal.contains(e.target)) return;
    // Find the associated input and close via its instance
    const id = openCal.id && openCal.id.replace(/^rs-date-calendar-/, '');
    const input = id && document.getElementById(id);
    const fp = input && input._flatpickr;
    if (fp) { fp.close(); return; }
    // Fallback: find any initialized input whose calendar is open
    document.querySelectorAll('input.flatpickr-input').forEach((inp) => {
      if (inp._flatpickr && inp._flatpickr.isOpen) inp._flatpickr.close();
    });
  }
  window.addEventListener('wheel', _scrollCloseHandler, { passive: true, capture: true });
  window.addEventListener('touchmove', _scrollCloseHandler, { passive: true, capture: true });

  /** Flatpickr year field (inside `.numInputWrapper`); defaults to `tabindex="-1"` unless we enable it. */
  function getFlatpickrYearInput(cal) {
    if (!cal) return null;
    return (
      cal.querySelector('input.numInput.cur-year') ||
      cal.querySelector('.flatpickr-current-month .numInputWrapper input')
    );
  }

  /** Invoice date-range fields (`page.invoices`); only these use `.rs-date-range___date-field`. */
  function isDateRangeFilterInput(input) {
    return (
      input.name === 'start_date' ||
      input.name === 'end_date' ||
      !!input.closest('.rs-date-range___date-field')
    );
  }

  /** Flatpickr disables `.cur-year` when min/max share a year; invoice filters need it editable. */
  function enableFlatpickrYearInput(cal) {
    const yearIn = getFlatpickrYearInput(cal);
    if (!yearIn) return;
    yearIn.removeAttribute('disabled');
    yearIn.setAttribute('tabindex', '0');
  }

  /**
   * Ordered focusables inside an open flatpickr calendar (controls then day cells).
   */
  function getCalendarFocusables(cal) {
    if (!cal) return [];

    const ordered = [];
    const prev = cal.querySelector('.flatpickr-prev-month');
    const month = cal.querySelector('.flatpickr-monthDropdown-months');
    const year = getFlatpickrYearInput(cal);
    const next = cal.querySelector('.flatpickr-next-month');

    [prev, month, year, next].forEach((el) => {
      if (el && el.getAttribute('tabindex') !== '-1') ordered.push(el);
    });

    cal.querySelectorAll('.flatpickr-days .dayContainer .flatpickr-day').forEach((day) => {
      if (day.getAttribute('tabindex') !== '-1') ordered.push(day);
    });

    return ordered;
  }

  /** Flatpickr’s month grid is 7 columns (weekdays). */
  const DAY_GRID_COLS = 7;

  /** Flatpickr adds .flatpickr-disabled to prev/next at min/max; they must not be tab stops. */
  function setPrevNextNavTabindex(el) {
    if (!el) return;
    el.setAttribute('tabindex', el.classList.contains('flatpickr-disabled') ? '-1' : '0');
  }

  /**
   * Only day cells inside each month `.dayContainer` under `.flatpickr-days`.
   * Excludes week-number labels: when `weekNumbers` is on, flatpickr injects extra
   * `<span class="flatpickr-day">` under `.flatpickr-weeks`, which breaks index math if
   * mixed with `cal.querySelectorAll(".flatpickr-day")`.
   */
  function getDayCellGrid(instance) {
    const root = instance?.daysContainer;
    if (root && root.children.length) {
      const cells = [];
      Array.from(root.children).forEach((monthEl) => {
        if (!monthEl.classList.contains('dayContainer')) return;
        Array.from(monthEl.children).forEach((child) => {
          if (child.classList?.contains('flatpickr-day')) cells.push(child);
        });
      });
      if (cells.length) return cells;
    }

    const cal = instance?.calendarContainer;
    if (!cal) return [];
    return Array.from(cal.querySelectorAll('.flatpickr-days .dayContainer .flatpickr-day'));
  }

  /** Arrow keys: not on disabled days or previous-month spillover (`.prevMonthDay`). */
  function isArrowFocusableDayCell(el) {
    return (
      el &&
      el.classList?.contains('flatpickr-day') &&
      !el.classList.contains('flatpickr-disabled') &&
      !el.classList.contains('prevMonthDay')
    );
  }

  const isNavigableDayEl = isArrowFocusableDayCell;

  /** Day cells under `.flatpickr-days` month grid (excludes week-number `.flatpickr-day` nodes). */
  function queryDaysSectionDayCells(cal) {
    if (!cal) return [];
    const root = cal.querySelector('.flatpickr-days');
    if (!root) return [];
    return Array.from(root.querySelectorAll('.dayContainer .flatpickr-day'));
  }

  /**
   * Roving tabindex: a single “active” day in `.flatpickr-days` is tabbable (`tabindex="0"`); all other
   * days (and week-number cells) are `tabindex="-1"`. Use arrows to move the active day.
   */
  function setFlatpickrDayTabStops(cal, instance, preferredActive) {
    if (!cal || !instance) return;
    const y = instance.currentYear;
    const m = instance.currentMonth;

    const isNavigableInMonthGrid = (el) => {
      if (!(el instanceof Element) || !el.classList.contains('flatpickr-day')) return false;
      if (!el.closest('.dayContainer')) return false;
      const inVisibleMonth =
        el.dateObj instanceof Date &&
        el.dateObj.getFullYear() === y &&
        el.dateObj.getMonth() === m;
      return inVisibleMonth && isArrowFocusableDayCell(el);
    };

    /** @type {Element | null} */
    let activeEl = null;

    if (preferredActive instanceof Element && isNavigableInMonthGrid(preferredActive)) {
      activeEl = preferredActive;
    }

    const ae = document.activeElement;
    if (!activeEl && ae instanceof Element && isNavigableInMonthGrid(ae)) {
      activeEl = ae;
    }

    if (!activeEl && instance.selectedDates?.length) {
      const sd = instance.selectedDates[0];
      for (const day of queryDaysSectionDayCells(cal)) {
        if (
          day.dateObj instanceof Date &&
          sameCalendarDate(day.dateObj, sd) &&
          isNavigableInMonthGrid(day)
        ) {
          activeEl = day;
          break;
        }
      }
    }

    if (!activeEl) {
      const today = cal.querySelector(
        '.flatpickr-days .dayContainer .flatpickr-day.today:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay)'
      );
      if (today && isNavigableInMonthGrid(today)) activeEl = today;
    }

    if (!activeEl) {
      const selected = cal.querySelector(
        '.flatpickr-days .dayContainer .flatpickr-day.selected:not(.flatpickr-disabled):not(.prevMonthDay)'
      );
      if (selected && isNavigableInMonthGrid(selected)) activeEl = selected;
    }

    if (!activeEl) {
      const grid = getDayCellGrid(instance);
      activeEl = grid.find((el) => isNavigableInMonthGrid(el)) || null;
    }

    cal.querySelectorAll('.flatpickr-days .flatpickr-day').forEach((day) => {
      const inMonthGrid = !!day.closest('.dayContainer');
      const tabbable = inMonthGrid && activeEl === day && isNavigableInMonthGrid(day);
      day.setAttribute('tabindex', tabbable ? '0' : '-1');
    });
  }

  /**
   * Move focus one step left/right, skipping disabled and `.prevMonthDay` cells.
   */
  function focusAdjacentEnabledDay(dayCells, fromIndex, direction) {
    let i = fromIndex + direction;
    while (i >= 0 && i < dayCells.length) {
      const cell = dayCells[i];
      if (isArrowFocusableDayCell(cell)) {
        cell.focus({ preventScroll: true });
        return true;
      }
      i += direction;
    }
    return false;
  }

  function sameCalendarDate(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  function getCalendarDatePlusDays(dayEl, days) {
    const d = dayEl.dateObj;
    if (!(d instanceof Date)) return null;
    const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    t.setDate(t.getDate() + days);
    return t;
  }

  /** Focus day 1 of the visible month if enabled, else first enabled day in that month. */
  function focusFirstDayOfVisibleMonth(instance, refreshTabStops) {
    refreshTabStops();
    const cells = getDayCellGrid(instance);
    const y = instance.currentYear;
    const m = instance.currentMonth;
    const inMonthEnabled = cells.filter(
      (el) =>
        el.dateObj instanceof Date &&
        el.dateObj.getFullYear() === y &&
        el.dateObj.getMonth() === m &&
        isArrowFocusableDayCell(el)
    );
    const firstOfMonth = inMonthEnabled.find((el) => el.dateObj.getDate() === 1);
    const target = firstOfMonth || inMonthEnabled[0];
    if (target) {
      target.focus({ preventScroll: true });
      return true;
    }
    return false;
  }

  function daysInCalendarMonth(year, monthIndex) {
    return new Date(year, monthIndex + 1, 0).getDate();
  }

  /** Focus last calendar day of the visible month if enabled, else latest enabled day in that month. */
  function focusLastDayOfVisibleMonth(instance, refreshTabStops) {
    refreshTabStops();
    const cells = getDayCellGrid(instance);
    const y = instance.currentYear;
    const m = instance.currentMonth;
    const lastN = daysInCalendarMonth(y, m);
    const inMonthEnabled = cells.filter(
      (el) =>
        el.dateObj instanceof Date &&
        el.dateObj.getFullYear() === y &&
        el.dateObj.getMonth() === m &&
        isArrowFocusableDayCell(el)
    );
    let match = inMonthEnabled.find((el) => el.dateObj.getDate() === lastN);
    if (!match && inMonthEnabled.length) {
      match = inMonthEnabled.reduce((a, b) =>
        a.dateObj.getDate() > b.dateObj.getDate() ? a : b
      );
    }
    if (match) {
      match.focus({ preventScroll: true });
      return true;
    }
    return false;
  }

  function getDayCellsInSameMonthContainer(dayEl) {
    const container = dayEl.closest('.dayContainer');
    if (!container) return [];
    return Array.from(container.children).filter((c) => c.classList?.contains('flatpickr-day'));
  }

  function isDayInFirstGridRow(dayEl) {
    const cellsInMonth = getDayCellsInSameMonthContainer(dayEl);
    const localIdx = cellsInMonth.indexOf(dayEl);
    return localIdx >= 0 && localIdx < DAY_GRID_COLS;
  }

  function isDayInLastRowLastColumn(dayEl) {
    const cellsInMonth = getDayCellsInSameMonthContainer(dayEl);
    const localIdx = cellsInMonth.indexOf(dayEl);
    if (localIdx === -1 || cellsInMonth.length === 0) return false;

    const lastCol = localIdx % DAY_GRID_COLS === DAY_GRID_COLS - 1;
    const row = Math.floor(localIdx / DAY_GRID_COLS);
    const lastRow = Math.floor((cellsInMonth.length - 1) / DAY_GRID_COLS);
    return lastCol && row === lastRow;
  }

  function isDayInFirstRowFirstColumn(dayEl) {
    const cellsInMonth = getDayCellsInSameMonthContainer(dayEl);
    const localIdx = cellsInMonth.indexOf(dayEl);
    if (localIdx === -1) return false;

    const firstCol = localIdx % DAY_GRID_COLS === 0;
    const firstRow = Math.floor(localIdx / DAY_GRID_COLS) === 0;
    return firstCol && firstRow;
  }

  /** Calendar day 1 of the month the picker is showing (not grid position—day 1 is often column 3–6). */
  function isFocusedFirstDayOfVisibleMonth(dayEl, instance) {
    const d = dayEl.dateObj;
    if (!(d instanceof Date)) return false;
    return (
      d.getFullYear() === instance.currentYear &&
      d.getMonth() === instance.currentMonth &&
      d.getDate() === 1
    );
  }

  /** True when the picker is showing this calendar month/year in the local timezone (“current month”). */
  function isInstanceViewingPresentMonth(instance) {
    const now = new Date();
    return (
      instance.currentYear === now.getFullYear() && instance.currentMonth === now.getMonth()
    );
  }

  /**
   * One calendar week up/down. Uses flatpickr’s `dateObj` on each cell instead of DOM index ±7,
   * so layout/CSS can’t desync arrow movement from the visible grid (which was skipping whole rows).
   * @returns {'moved'|'aboveGrid'|'belowGrid'}
   */
  function focusVerticalDay(dayCells, activeEl, rowDelta) {
    const fromDate = activeEl.dateObj;
    if (fromDate instanceof Date) {
      let targetDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      targetDate.setDate(targetDate.getDate() + rowDelta * DAY_GRID_COLS);

      for (let guard = 0; guard < 8; guard++) {
        const match = dayCells.find(
          (el) => el.dateObj instanceof Date && sameCalendarDate(el.dateObj, targetDate)
        );
        if (!match) {
          return rowDelta < 0 ? 'aboveGrid' : 'belowGrid';
        }
        if (isArrowFocusableDayCell(match)) {
          match.focus({ preventScroll: true });
          return 'moved';
        }
        targetDate.setDate(targetDate.getDate() + rowDelta * DAY_GRID_COLS);
      }

      return rowDelta < 0 ? 'aboveGrid' : 'belowGrid';
    }

    const fromIndex = dayCells.indexOf(activeEl);
    if (fromIndex === -1) return rowDelta < 0 ? 'aboveGrid' : 'belowGrid';

    let i = fromIndex + rowDelta * DAY_GRID_COLS;
    while (i >= 0 && i < dayCells.length) {
      if (isArrowFocusableDayCell(dayCells[i])) {
        dayCells[i].focus({ preventScroll: true });
        return 'moved';
      }
      i += rowDelta * DAY_GRID_COLS;
    }

    return rowDelta < 0 ? 'aboveGrid' : 'belowGrid';
  }

  function getHeaderFocusOrder(cal) {
    const prev = cal.querySelector('.flatpickr-prev-month');
    const month = cal.querySelector('.flatpickr-monthDropdown-months');
    const year = getFlatpickrYearInput(cal);
    const next = cal.querySelector('.flatpickr-next-month');
    return [prev, month, year, next].filter((el) => el && el.getAttribute('tabindex') !== '-1');
  }

  function focusFirstLogicalDay(cal) {
    const day =
      cal.querySelector('.flatpickr-day.selected:not(.flatpickr-disabled):not(.prevMonthDay)') ||
      cal.querySelector('.flatpickr-day.today:not(.flatpickr-disabled):not(.prevMonthDay)') ||
      cal.querySelector('.flatpickr-day:not(.flatpickr-disabled):not(.prevMonthDay)');
    day?.focus({ preventScroll: true });
  }

  /** Prefer the selected date, then today, then the first enabled date. */
  function focusTodayOrFirstLogical(cal) {
    const selected = cal.querySelector(
      '.flatpickr-day.selected:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay)'
    );
    if (selected) {
      selected.focus({ preventScroll: true });
      return;
    }
    const today = cal.querySelector(
      '.flatpickr-day.today:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay)'
    );
    if (today) {
      today.focus({ preventScroll: true });
      return;
    }
    focusFirstLogicalDay(cal);
  }

  const DATE_ERR_SVG =
    '<svg class="rs-icon rs-icon-error" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 48 48"><path d="M48.0001 48.2632H6.10352e-05V0.263184H48.0001V48.2632ZM24.1407 32.5972C23.2704 32.5972 22.5874 32.8512 22.0919 33.3599C21.6097 33.8554 21.3682 34.4719 21.3682 35.2085C21.3682 35.9183 21.6097 36.5277 22.0919 37.0366C22.5874 37.5321 23.2703 37.7798 24.1407 37.7798C24.9978 37.7798 25.6744 37.5322 26.17 37.0366C26.6655 36.5277 26.9131 35.9183 26.9131 35.2085C26.9131 34.4719 26.6655 33.8554 26.17 33.3599C25.6745 32.851 24.9978 32.5972 24.1407 32.5972ZM21.5089 8.24854L22.0118 28.8804H26.1495L26.6319 8.24854H21.5089Z" fill="currentColor"/></svg>';

  const DATE_ERROR_CLASSES = [
    'rs-date-format-error',
    'rs-date-min-date-error',
    'rs-date-max-year-error',
  ];

  function setDateInputAriaInvalid(input, invalid) {
    const value = invalid ? 'true' : 'false';
    input.setAttribute('aria-invalid', value);
    const fp = /** @type {any} */ (input)._flatpickr;
    fp?.altInput?.setAttribute('aria-invalid', value);
  }

  // It is for past date announce ment issue
  function addDescribedBy(element, id) {
  if (!element || !id) return;

  const ids = new Set(
    (element.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean)
  );

  ids.add(id);
  element.setAttribute('aria-describedby', [...ids].join(' '));
}

function removeDescribedBy(element, id) {
  if (!element || !id) return;

  const ids = (element.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter(Boolean)
    .filter((existingId) => existingId !== id);

  if (ids.length) {
    element.setAttribute('aria-describedby', ids.join(' '));
  } else {
    element.removeAttribute('aria-describedby');
  }
}

function getDateErrorId(input, cls) {
  if (!input.id) {
    input.id = `rs-date-input-${Math.random().toString(36).slice(2, 10)}`;
  }

  return `${input.id}-${cls}`;
}


  // function showDateError(input, cls, message) {
  //   const wrapper = input.closest('.rs-input__content');
  //   if (!(wrapper instanceof HTMLElement)) return;
  //   DATE_ERROR_CLASSES.forEach((errorClass) => {
  //     if (errorClass !== cls) wrapper.querySelector('.' + errorClass)?.remove();
  //   });
  //   let err = wrapper.querySelector('.' + cls);
  //   if (!err) {
  //     err = document.createElement('div');
  //     err.className =
  //       cls + ' rs-flex rs-align-center rs-gap-4 rs-input__error status-red text-small rs-error__message weight-500';
  //     err.setAttribute('role', 'alert');
  //     err.innerHTML = DATE_ERR_SVG + '<span></span>';
  //     const inputWrapper = wrapper.querySelector('.rs-input__wrapper');
  //     if (inputWrapper) {
  //       inputWrapper.insertAdjacentElement('afterend', err);
  //     } else {
  //       wrapper.appendChild(err);
  //     }
  //   }
  //   const span = err.querySelector('span');
  //   if (span) span.textContent = message;
  //   wrapper.classList.add('rs-error');
  //   setDateInputAriaInvalid(input, true);
  // }
  function showDateError(input, cls, message) {
  const wrapper = input.closest('.rs-input__content');
  if (!(wrapper instanceof HTMLElement)) return;

  DATE_ERROR_CLASSES.forEach((errorClass) => {
    if (errorClass !== cls) {
      const otherError = wrapper.querySelector('.' + errorClass);

      if (otherError) {
        removeDescribedBy(input, otherError.id);
        removeDescribedBy(input._flatpickr?.altInput, otherError.id);
        otherError.remove();
      }
    }
  });

  let err = wrapper.querySelector('.' + cls);

  if (!err) {
    err = document.createElement('div');
    err.className =
      cls +
      ' rs-flex rs-align-center rs-gap-4 rs-input__error ' +
      'status-red text-small rs-error__message weight-500';

    err.id = getDateErrorId(input, cls);
    err.setAttribute('role', 'alert');
    err.setAttribute('aria-live', 'assertive');
    err.innerHTML = DATE_ERR_SVG + '<span></span>';

    const inputWrapper = wrapper.querySelector('.rs-input__wrapper');

    if (inputWrapper) {
      inputWrapper.insertAdjacentElement('afterend', err);
    } else {
      wrapper.appendChild(err);
    }
  }

  const span = err.querySelector('span');
  if (span) span.textContent = message;

  wrapper.classList.add('rs-error');
  setDateInputAriaInvalid(input, true);

  // Connect the persistent error to both inputs.
  addDescribedBy(input, err.id);
  addDescribedBy(input._flatpickr?.altInput, err.id);
}

  // function clearDateError(input, cls) {
  //   const wrapper = input.closest('.rs-input__content');
  //   if (!(wrapper instanceof HTMLElement)) return;
  //   const err = wrapper.querySelector('.' + cls);
  //   if (err) err.remove();
  //   const hasDateError = DATE_ERROR_CLASSES.some((errorClass) => wrapper.querySelector('.' + errorClass));
  //   if (!hasDateError && !wrapper.querySelector('.rs-error__message')) {
  //     wrapper.classList.remove('rs-error');
  //   }
  //   if (!hasDateError) setDateInputAriaInvalid(input, false);
  // }
  function clearDateError(input, cls) {
  const wrapper = input.closest('.rs-input__content');
  if (!(wrapper instanceof HTMLElement)) return;

  const err = wrapper.querySelector('.' + cls);

  if (err) {
    removeDescribedBy(input, err.id);
    removeDescribedBy(input._flatpickr?.altInput, err.id);
    err.remove();
  }

  const hasDateError = DATE_ERROR_CLASSES.some((errorClass) =>
    wrapper.querySelector('.' + errorClass)
  );

  if (!hasDateError && !wrapper.querySelector('.rs-error__message')) {
    wrapper.classList.remove('rs-error');
  }

  if (!hasDateError) {
    setDateInputAriaInvalid(input, false);
  }
}

  function clearAllDateErrors(input) {
    DATE_ERROR_CLASSES.forEach((cls) => clearDateError(input, cls));
  }

  function showDateFormatError(input, message) {
    showDateError(input, 'rs-date-format-error', message);
  }

  function showDateFutureError(input, message) {
    showDateError(input, 'rs-date-max-year-error', message);
  }

  function showDatePastError(input, message) {
    showDateError(input, 'rs-date-min-date-error', message);
  }

  /** Format-only placeholders (mm/dd/yyyy) must not become the accessible name. */
  function isDateMaskPlaceholder(text) {
    const t = String(text || '')
      .trim()
      .toLowerCase()
      .replace(/\s/g, '');
    if (!t) return false;
    return (
      /^m{1,2}[/.-]d{1,2}[/.-]y{2,4}$/.test(t) ||
      /^d{1,2}[/.-]m{1,2}[/.-]y{2,4}$/.test(t) ||
      /^y{4}[/.-]m{1,2}[/.-]d{1,2}$/.test(t)
    );
  }

  function normalizeLabelText(raw) {
    return raw
      .replace(/\s+/g, ' ')
      .replace(/\s*\*\s*$/, '')
      .trim();
  }

  /** Visible field name for ARIA (native input may be hidden when flatpickr adds `altInput`). */
  function getDateFieldAccessibleName(dateInput) {
    const explicit = dateInput.getAttribute('aria-label');
    if (explicit && explicit.trim() && !isDateMaskPlaceholder(explicit)) {
      return explicit.trim();
    }

    const tryLabelEl = (lab) => {
      if (!lab) return '';
      const text = normalizeLabelText(lab.textContent || '');
      return text && !isDateMaskPlaceholder(text) ? text : '';
    };

    if (dateInput.labels && dateInput.labels.length > 0) {
      const fromLabels = tryLabelEl(dateInput.labels[0]);
      if (fromLabels) return fromLabels;
    }

    const id = dateInput.id;
    if (id && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      const lab = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      const fromFor = tryLabelEl(lab);
      if (fromFor) return fromFor;
    }

    const wrap = dateInput.closest('.rs-input__content');
    if (wrap) {
      if (id && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        const scoped = wrap.querySelector(`label.rs-input__label[for="${CSS.escape(id)}"]`);
        const fromScoped = tryLabelEl(scoped);
        if (fromScoped) return fromScoped;
      }
      const fallbackScoped = wrap.querySelector('label.rs-input__label');
      const fromFallback = tryLabelEl(fallbackScoped);
      if (fromFallback) return fromFallback;
    }

    const ph = dateInput.getAttribute('placeholder');
    if (ph && ph.trim() && !isDateMaskPlaceholder(ph)) return ph.trim();

    return (window.__t && window.__t('date_input_default_label')) || 'Date';
  }

  function getCalendarDialogAriaLabel(instance, dateInput) {
    const name = getDateFieldAccessibleName(dateInput);
    const months = instance.l10n?.months?.longhand;
    const monthName =
      months && months[instance.currentMonth] != null
        ? months[instance.currentMonth]
        : String(instance.currentMonth + 1);
    const suffix =
      (window.__t && window.__t('date_picker_calendar_suffix')) || 'calendar';
    return `${name}, ${monthName} ${instance.currentYear} — ${suffix}`;
  }

  function refreshCalendarDayAriaLabels(instance) {
    const cal = instance.calendarContainer;
    if (!cal) return;
    queryDaysSectionDayCells(cal).forEach((day) => {
      const d = day.dateObj;
      if (!(d instanceof Date)) return;
      const isDisabled = day.classList.contains('flatpickr-disabled');
      let label = d.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (isDisabled) {
        const unavail =
          (window.__t && window.__t('date_picker_day_unavailable')) || 'Unavailable';
        label += `, ${unavail}`;
      }
      day.setAttribute('aria-label', label);
    });
  }

  /**
   * Flatpickr uses `<span>` for month arrows; VoiceOver needs `role="button"` and decorative
   * children hidden or it reads the SVG/unicode arrow instead of `aria-label`.
   */
  function setFlatpickrMonthNavButtonA11y(el, label) {
    if (!el) return;
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', label);
    el.setAttribute(
      'tabindex',
      el.classList.contains('flatpickr-disabled') ? '-1' : '0'
    );
    if (el.classList.contains('flatpickr-disabled')) {
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('aria-disabled');
    }
    Array.from(el.children).forEach((child) => {
      child.setAttribute('aria-hidden', 'true');
    });
    el.querySelectorAll('svg').forEach((svg) => {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
  }

  function applyCalendarControlAriaLabels(cal) {
    if (!cal) return;
    reduceFlatpickrGroupAnnouncement(cal);
    const labelPrev =
      (window.__t && window.__t('date_picker_previous_month')) || 'Previous Month';
    const labelNext = (window.__t && window.__t('date_picker_next_month')) || 'Next month';
    cal.querySelectorAll('.flatpickr-prev-month').forEach((el) => {
      setFlatpickrMonthNavButtonA11y(el, labelPrev);
    });
    cal.querySelectorAll('.flatpickr-next-month').forEach((el) => {
      setFlatpickrMonthNavButtonA11y(el, labelNext);
    });
    const months = cal.querySelector('.flatpickr-monthDropdown-months');
    const yearIn = getFlatpickrYearInput(cal);
    if (months) {
      months.setAttribute(
        'aria-label',
        (window.__t && window.__t('date_picker_choose_month')) || 'Month'
      );
    }
    if (yearIn) {
      yearIn.setAttribute(
        'aria-label',
        (window.__t && window.__t('date_picker_choose_year')) || 'Year'
      );
    }
    const weekdays = cal.querySelectorAll('.flatpickr-weekday');
    weekdays.forEach((w) => {
      if (!w.getAttribute('aria-label')) {
        const abbrev = (w.textContent || '').trim();
        if (abbrev) w.setAttribute('aria-label', abbrev);
      }
    });
  }

  function syncDatePickerExpanded(instance, dateInput, open) {
    const focusEl = instance.altInput || dateInput;
    if (focusEl) focusEl.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * Flatpickr hides the native input and shows `altInput`. Copy the format placeholder onto
   * `altInput` and strip it from the native field so the mask is not read twice by SRs.
   * Removes legacy `.rs-input__date-format-hint` overlay spans if present.
   */
  function setupAltInputPlaceholder(instance, nativeInput) {
    const wrap = nativeInput.closest('.rs-input__wrapper');
    if (wrap) {
      wrap.querySelectorAll('.rs-input__date-format-hint').forEach((el) => el.remove());
      wrap.classList.remove('rs-input__wrapper--show-date-format-hint');
    }

    const alt = instance.altInput;
    if (!alt) return;

    const translated =
      window.__t && window.__t('date_placeholder');
    const formatHint =
      (translated && translated !== 'date_placeholder' ? translated : '') ||
      (nativeInput.getAttribute('placeholder') || '').trim() ||
      'mm/dd/yyyy';
    nativeInput.removeAttribute('placeholder');
    alt.setAttribute('placeholder', formatHint);
  }

  /** Flatpickr often mirrors the date mask into `aria-label` on `altInput`; replace with the real field label. */
  function applyFlatpickrTriggerAriaLabel(instance, dateInput) {
    const focusEl = instance.altInput || dateInput;
    if (!focusEl) return;

    const native = instance.input || dateInput;
    if (instance.altInput && native instanceof HTMLElement && native !== instance.altInput) {
      ['aria-describedby', 'aria-labelledby', 'aria-required', 'aria-invalid'].forEach((attr) => {
        const v = native.getAttribute(attr);
        if (v != null && String(v).trim() !== '') {
          instance.altInput.setAttribute(attr, v);
        } else {
          instance.altInput.removeAttribute(attr);
        }
      });
    }

    if (focusEl.getAttribute('aria-labelledby')) {
      focusEl.removeAttribute('aria-label');
      return;
    }

    const fieldName = getDateFieldAccessibleName(dateInput);
    const existingAria = focusEl.getAttribute('aria-label');
    if (
      !existingAria ||
      !String(existingAria).trim() ||
      isDateMaskPlaceholder(existingAria)
    ) {
      focusEl.setAttribute('aria-label', fieldName);
    }
  }

  /**
   * Flatpickr sets `tabIndex=-1` on `.flatpickr-calendar` and `.flatpickr-days` for focus tricks.
   * On WebKit those focusable layout divs become AXGroup → VoiceOver announces "group".
   * Mark structural wrappers presentational and drop redundant tabindex so SRs land on real controls.
   */
  function reduceFlatpickrGroupAnnouncement(cal) {
    if (!cal) return;
    cal.removeAttribute('tabindex');
    cal.querySelectorAll(
      '.flatpickr-months, .flatpickr-month, .flatpickr-innerContainer, .flatpickr-rContainer, .flatpickr-weekdays, .flatpickr-weekdaycontainer'
    ).forEach((el) => {
      el.removeAttribute('tabindex');
      el.setAttribute('role', 'presentation');
    });
    cal.querySelectorAll('.flatpickr-days').forEach((el) => {
      el.removeAttribute('tabindex');
      el.setAttribute('role', 'presentation');
    });
  }

  /**
   * Hook once after flatpickr builds the DOM: calendar landmark + trigger association + control/day labels.
   */
  function wireFlatpickrAria(instance, dateInput) {
    const cal = instance.calendarContainer;
    const focusEl = instance.altInput || dateInput;
    if (!cal || !focusEl) return;

    const content = dateInput.closest('.rs-input__content');
    if (content) content.removeAttribute('aria-labelledby');

    const calId =
      dateInput.id != null && String(dateInput.id)
        ? `rs-date-calendar-${dateInput.id}`
        : `rs-date-calendar-${Math.random().toString(36).slice(2, 11)}`;

    cal.setAttribute('id', calId);
    cal.removeAttribute('role');
    cal.removeAttribute('aria-modal');
    cal.setAttribute('aria-label', getCalendarDialogAriaLabel(instance, dateInput));

    applyFlatpickrTriggerAriaLabel(instance, dateInput);
    focusEl.setAttribute('aria-haspopup', 'true');
    focusEl.setAttribute('aria-controls', calId);
    syncDatePickerExpanded(instance, dateInput, !!instance.isOpen);

    reduceFlatpickrGroupAnnouncement(cal);
    applyCalendarControlAriaLabels(cal);
    refreshCalendarDayAriaLabels(instance);
  }

  function getFlatpickrConfig(input) {
    const wrapper = input.closest('.rs-input__content');
    const isWarrantyTable = wrapper?.classList.contains('rs-warranty-table__input');
    const modal = input.closest('.rs-modal');
    const deliveryDatesModal = input.closest('.delivery-dates-modal');

    const isDateRangeFilter = isDateRangeFilterInput(input);

    /** Pick dates from the calendar only — typing would open the software keyboard on phones. */
    const suppressDateKeyboard =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches;

    let manualMinDate = null;
    let manualMaxDate = null;
    let showManualRangeErrors = false;

    function calendarDay(date) {
      return date instanceof Date
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
        : null;
    }

    function clearPickerValuePreservingDisplay(instance, visibleInput, display) {
      const text = display || '';
      instance.clear(false);
      input.value = '';
      visibleInput.value = text;
      // Flatpickr may update the visible field after blur. Re-assert out-of-range
      // values so the user can correct them alongside the range error.
      requestAnimationFrame(() => {
        if (visibleInput.value !== text) visibleInput.value = text;
      });
    }

    function validateManualDateResult(result, visibleInput, instance) {
      if (!result || result.status === 'empty') {
        clearAllDateErrors(input);
        instance.clear(false);
        return;
      }

      if (result.status !== 'valid') {
        const message =
          (window.__t && window.__t('date_input_validation_date_invalid')) ||
          'Invalid date';
        showDateFormatError(input, message);
        clearPickerValuePreservingDisplay(instance, visibleInput, result.display);
        return;
      }

      const parsedDay = calendarDay(result.date);
      const minDay = calendarDay(manualMinDate);
      const maxDay = calendarDay(manualMaxDate);

      if (showManualRangeErrors && maxDay && parsedDay > maxDay) {
        const message =
          (window.__t && window.__t('date_input_validation_date_future')) ||
          'Date must be within 2 years';
        showDateFutureError(input, message);
        clearPickerValuePreservingDisplay(instance, visibleInput, result.display);
        return;
      }

      if (showManualRangeErrors && minDay && parsedDay < minDay) {
        const message =
          (window.__t && window.__t('date_input_validation_date_past')) ||
          'Date cannot be in the past';
        showDatePastError(input, message);
        clearPickerValuePreservingDisplay(instance, visibleInput, result.display);
        const row = input.closest('.rs-table-body__row');
        if (row) row.classList.add('prev-date-product');
        document.querySelector('.cart-page-actions__prev-date-banner')?.classList.add('rs-banner--visible');
        return;
      }

      clearAllDateErrors(input);
      const withinMin = !minDay || parsedDay >= minDay;
      const withinMax = !maxDay || parsedDay <= maxDay;
      if (withinMin && withinMax) {
        instance.setDate(result.date, false, 'm/d/Y');
        visibleInput.value = result.display;
      } else {
        clearPickerValuePreservingDisplay(instance, visibleInput, result.display);
      }
    }

    const config = {
      dateFormat: 'm/d/Y',
      altInput: true,
      altFormat: 'm/d/Y',
      altInputClass: 'rs-input rs-input-type__date text-medium text-primary',
      allowInput: !suppressDateKeyboard,
      disableMobile: true,
      parseDate: (datestr, format) => flatpickr.parseDate(datestr, format),
      position: function(fpInstance, customPositionElement) {
        const posEl = customPositionElement || fpInstance.altInput || fpInstance.input;
        const rect = posEl.getBoundingClientRect();
        const cal = fpInstance.calendarContainer;
        const calH = cal.offsetHeight;
        const calW = cal.offsetWidth;
        const appendTo = fpInstance.config.appendTo;
        const opensAbove = window.innerHeight - rect.bottom < calH && rect.top > calH;
        let top, left;
        if (!appendTo || appendTo === document.body) {
          const scrollY = window.pageYOffset;
          const scrollX = window.pageXOffset;
          top = opensAbove
            ? scrollY + rect.top - calH - 2
            : scrollY + rect.bottom + 2;
          left = Math.max(0, Math.min(rect.left + scrollX, window.innerWidth + scrollX - calW - 2));
        } else {
          const containerRect = appendTo.getBoundingClientRect();
          top = opensAbove
            ? rect.top - containerRect.top - calH - 2
            : rect.bottom - containerRect.top + 2;
          left = rect.left - containerRect.left;
        }
        cal.style.top = `${top}px`;
        cal.style.left = `${left}px`;
      },

      onReady: function (selectedDates, dateStr, instance) {
        if (suppressDateKeyboard && instance.altInput) {
          instance.altInput.readOnly = true;
          instance.altInput.setAttribute('inputmode', 'none');
          instance.altInput.setAttribute('autocomplete', 'off');
        }
        setupCalendarA11y(instance, input);
        setupAltInputPlaceholder(instance, input);
        if (
          typeof window.attachDateAutoFormat === 'function' &&
          instance.altInput &&
          !suppressDateKeyboard
        ) {
          window.attachDateAutoFormat(instance.altInput, {
            onCommit: (result, visibleInput) => {
              // Blur runs before the day `click` when focus moves into the open calendar.
              // clear()/setDate redraws days and cancels Flatpickr's selectDate (needs a 2nd click).
              if (instance.isOpen) return;
              validateManualDateResult(result, visibleInput, instance);
            },
          });
        }
        const triggerInputs = [input, instance.altInput].filter(Boolean);
        const inputWrapper = input.closest('.rs-input__wrapper');
        const calendarIcon = inputWrapper?.querySelector('.rs-icon__input-right');

        triggerInputs.forEach(function(triggerInput) {
          triggerInput.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              instance.open();
            }
          });
        });

        if (calendarIcon) {
          calendarIcon.style.cursor = 'pointer';
          calendarIcon.style.pointerEvents = 'auto';

          calendarIcon.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            instance.open();
          });
        }
      },

      onOpen: function (selectedDates, dateStr, instance) {
        const cal = instance.calendarContainer;
        if (!cal) return;

        if (deliveryDatesModal) {
          cal.style.zIndex = '1000001';
        }

        setFlatpickrDayTabStops(cal, instance);

        setPrevNextNavTabindex(cal.querySelector('.flatpickr-prev-month'));
        setPrevNextNavTabindex(cal.querySelector('.flatpickr-next-month'));
        const months = cal.querySelector('.flatpickr-monthDropdown-months');
        if (months) months.setAttribute('tabindex', '0');
        if (isDateRangeFilter) {
          enableFlatpickrYearInput(cal);
        } else {
          const yearIn = getFlatpickrYearInput(cal);
          if (yearIn) yearIn.setAttribute('tabindex', '0');
        }
      },

    };

    if (deliveryDatesModal) {
      config.appendTo = document.body;
    } else if (modal) {
      config.appendTo = modal;
    }

    if (isWarrantyTable) {
      config.maxDate = 'today';
      manualMaxDate = new Date();
    } else if (isDateRangeFilter) {
      config.maxDate = 'today';
      manualMaxDate = new Date();

      const keepYearInputEnabled = (selectedDates, dateStr, instance) => {
        enableFlatpickrYearInput(instance.calendarContainer);
      };

      const prevOnOpenForRange = config.onOpen;
      config.onOpen = function (selectedDates, dateStr, instance) {
        prevOnOpenForRange.call(this, selectedDates, dateStr, instance);
        enableFlatpickrYearInput(instance.calendarContainer);
      };

      const prevOnReadyForRange = config.onReady;
      config.onReady = function (selectedDates, dateStr, instance) {
        prevOnReadyForRange.call(this, selectedDates, dateStr, instance);
        enableFlatpickrYearInput(instance.calendarContainer);
      };

      config.onMonthChange = [...(config.onMonthChange || []), keepYearInputEnabled];
      config.onYearChange = [...(config.onYearChange || []), keepYearInputEnabled];
      config.onChange = [...(config.onChange || []), keepYearInputEnabled];
    } else {
      config.minDate = 'today';
      manualMinDate = new Date();
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 2);
      config.maxDate = maxDate;
      manualMaxDate = maxDate;
      showManualRangeErrors = true;

      config.onChange = function (selectedDates) {
        if (selectedDates.length > 0) {
          clearAllDateErrors(input);
        }
      };

    }

    return config;
  }

  function setupCalendarA11y(instance, dateInput) {
    wireFlatpickrAria(instance, dateInput);

    function refreshCalendarAriaPresentation() {
      const cal = instance.calendarContainer;
      if (!cal) return;
      cal.removeAttribute('role');
      cal.removeAttribute('aria-modal');
      cal.setAttribute('aria-label', getCalendarDialogAriaLabel(instance, dateInput));
      reduceFlatpickrGroupAnnouncement(cal);
      applyCalendarControlAriaLabels(cal);
      refreshCalendarDayAriaLabels(instance);
      applyFlatpickrTriggerAriaLabel(instance, dateInput);
    }

    const isWarrantyReturnsPage = dateInput
      .closest('.rs-input__content')
      ?.classList.contains('rs-warranty-table__input');
    const isDateRangeFilter = isDateRangeFilterInput(dateInput);

    const getPrevBtn = () => instance.calendarContainer?.querySelector('.flatpickr-prev-month');
    const getNextBtn = () => instance.calendarContainer?.querySelector('.flatpickr-next-month');
    const getMonthDropdown = () => instance.calendarContainer?.querySelector('.flatpickr-monthDropdown-months');
    const getYearInput = () => getFlatpickrYearInput(instance.calendarContainer);

    function consumeCalendarArrowKeydown(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }

    const refreshTabStops = (preferredDay) => {
      const cal = instance.calendarContainer;
      if (!cal) return;

      setPrevNextNavTabindex(getPrevBtn());
      const monthEl = getMonthDropdown();
      if (monthEl) monthEl.setAttribute('tabindex', '0');
      if (isDateRangeFilter) {
        enableFlatpickrYearInput(cal);
      } else {
        const yearEl = getYearInput();
        if (yearEl) yearEl.setAttribute('tabindex', '0');
      }
      setPrevNextNavTabindex(getNextBtn());

      setFlatpickrDayTabStops(cal, instance, preferredDay);
    };

    instance.calendarContainer?.addEventListener('focusin', (e) => {
      if (!instance.isOpen) return;
      const t = e.target;
      if (
        t instanceof Element &&
        t.classList.contains('flatpickr-day') &&
        t.closest('.flatpickr-days')
      ) {
        refreshTabStops(t);
      }
    });

    const monthSelect = getMonthDropdown();
    if (monthSelect) {
      monthSelect.setAttribute('tabindex', '0');
    }

    const focusableInput = instance.altInput || dateInput;

    let docFocusInHandler = null;

    function detachCalendarFocusTrap() {
      if (!docFocusInHandler) return;
      document.removeEventListener('focusin', docFocusInHandler, true);
      docFocusInHandler = null;
    }

    function attachCalendarFocusTrap() {
      detachCalendarFocusTrap();
      docFocusInHandler = (e) => {
        if (!instance.isOpen) return;
        const cal = instance.calendarContainer;
        if (!cal) return;
        const target = e.target;
        if (!(target instanceof Element)) return;
        if (cal.contains(target)) return;
        const inputEl = instance.altInput || dateInput;
        if (target === inputEl) return;

        requestAnimationFrame(() => {
          if (!instance.isOpen) return;
          if (cal.contains(document.activeElement)) return;
          refreshTabStops();
          focusTodayOrFirstLogical(cal);
        });
      };
      document.addEventListener('focusin', docFocusInHandler, true);
    }

    function callHook(hook, ctx, args) {
      if (typeof hook === 'function') hook.apply(ctx, args);
      else if (Array.isArray(hook)) hook.forEach((fn) => fn.apply(ctx, args));
    }

    const prevOnOpen = instance.config.onOpen;
    instance.config.onOpen = function (selectedDates, dateStr, inst) {
      callHook(prevOnOpen, this, [selectedDates, dateStr, inst]);
      attachCalendarFocusTrap();
      refreshCalendarAriaPresentation();
      syncDatePickerExpanded(instance, dateInput, true);
    };

    const prevOnClose = instance.config.onClose;
    instance.config.onClose = function (selectedDates, dateStr, inst) {
      detachCalendarFocusTrap();
      syncDatePickerExpanded(instance, dateInput, false);
      callHook(prevOnClose, this, [selectedDates, dateStr, inst]);
    };

    focusableInput.addEventListener('keydown', (e) => {
      if (!instance.isOpen) return;

      const cal = instance.calendarContainer;
      if (!cal) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopImmediatePropagation();
        instance.close();
        requestAnimationFrame(() => focusableInput.focus({ preventScroll: true }));
        return;
      }

      if (e.key === 'ArrowDown' && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const active = document.activeElement;
        if (active === focusableInput || active === dateInput) {
          e.preventDefault();
          e.stopImmediatePropagation();
          refreshTabStops();
          focusTodayOrFirstLogical(cal);
        }
        return;
      }

      if (e.key !== 'Tab') return;

      refreshTabStops();
      const focusables = getCalendarFocusables(cal);
      if (focusables.length === 0) return;

      e.preventDefault();
      if (e.shiftKey) {
        focusables[focusables.length - 1]?.focus({ preventScroll: true });
      } else {
        const monthPicker = getMonthDropdown();
        if (monthPicker) {
          monthPicker.focus({ preventScroll: true });
        } else {
          focusTodayOrFirstLogical(cal);
        }
      }
    });

    instance.calendarContainer?.addEventListener(
      'keydown',
      (e) => {
        if (!instance.isOpen || e.key !== 'Tab') return;
        const cal = instance.calendarContainer;
        if (!cal) return;

        refreshTabStops();
        const focusables = getCalendarFocusables(cal);
        const active = document.activeElement;
        const activeIndex = focusables.indexOf(active);
        if (activeIndex === -1 || focusables.length === 0) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        if (e.shiftKey) {
          const activeIsDay =
            active instanceof Element &&
            active.classList.contains('flatpickr-day') &&
            !!active.closest('.dayContainer');
          if (activeIsDay) {
            const header = getHeaderFocusOrder(cal);
            header[header.length - 1]?.focus({ preventScroll: true });
          } else if (activeIndex > 0) {
            focusables[activeIndex - 1]?.focus({ preventScroll: true });
          } else {
            focusableInput.focus({ preventScroll: true });
          }
          return;
        }

        if (activeIndex < focusables.length - 1) {
          focusables[activeIndex + 1]?.focus({ preventScroll: true });
        } else {
          const monthPicker = getMonthDropdown();
          (monthPicker || focusables[0])?.focus({ preventScroll: true });
        }
      },
      true
    );

    instance.calendarContainer?.addEventListener(
      'keydown',
      (e) => {
        if (!instance.isOpen) return;

        const cal = instance.calendarContainer;
        const active = document.activeElement;
        if (!cal || !(active instanceof Element) || !cal.contains(active)) return;

        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          e.stopImmediatePropagation();
          instance.close();
          requestAnimationFrame(() => focusableInput.focus({ preventScroll: true }));
          return;
        }

        const dayEl = active.classList.contains('flatpickr-day')
          ? active
          : active.closest('.flatpickr-day');
        if (
          !dayEl ||
          !dayEl.closest('.dayContainer') ||
          dayEl.classList.contains('flatpickr-disabled')
        ) {
          return;
        }

        if (
          e.key === 'Enter' ||
          e.key === ' ' ||
          e.key === 'Space' ||
          e.key === 'Spacebar' ||
          e.code === 'Space'
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
          dayEl.click();
          window.setTimeout(() => {
            if (instance.isOpen) instance.close();
            focusableInput.focus({ preventScroll: true });
          }, 0);
        }
      },
      true
    );

    instance.calendarContainer?.addEventListener('click', (e) => {
      if (!instance.isOpen || !(e.target instanceof Element)) return;
      const dayEl = e.target.closest('.flatpickr-day');
      if (
        !dayEl ||
        !dayEl.closest('.dayContainer') ||
        dayEl.classList.contains('flatpickr-disabled') ||
        !(dayEl.dateObj instanceof Date)
      ) {
        return;
      }

      const selectedDate = instance.selectedDates?.[0];
      if (!(selectedDate instanceof Date) || !sameCalendarDate(selectedDate, dayEl.dateObj)) {
        instance.setDate(dayEl.dateObj, true);
      }
      instance.close();
    });

    // Capture: flatpickr also listens on this node and moves by ±1 / ±7 on arrows; without
    // stopImmediatePropagation, every keypress runs our nav and then flatpickr’s (skips rows).
    instance.calendarContainer?.addEventListener(
      'keydown',
      (e) => {
        if (!instance.isOpen) return;

        const key = e.key;
        if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'ArrowUp' && key !== 'ArrowDown') {
          return;
        }

        const cal = instance.calendarContainer;
        if (!cal) return;

        const active = document.activeElement;
        if (!(active instanceof Element) || !cal.contains(active)) return;

        if (!active.closest('.dayContainer')) {
          return;
        }

        const dayEl = active.classList.contains('flatpickr-day')
          ? active
          : active.closest('.flatpickr-day');
        if (!dayEl || !cal.contains(dayEl)) return;

        const dayCells = getDayCellGrid(instance);
        const dayIndex = dayCells.indexOf(dayEl);
        if (dayIndex === -1) return;

        if (!isNavigableDayEl(dayEl)) {
          consumeCalendarArrowKeydown(e);
          return;
        }

        if (key === 'ArrowLeft') {
          consumeCalendarArrowKeydown(e);

          if (
            (isDayInFirstRowFirstColumn(dayEl) || isFocusedFirstDayOfVisibleMonth(dayEl, instance)) &&
            (isWarrantyReturnsPage || !isInstanceViewingPresentMonth(instance)) &&
            typeof instance.changeMonth === 'function'
          ) {
            const prevCalendarDay = getCalendarDatePlusDays(dayEl, -1);
            const minDate = instance.config?.minDate;
            let blockedByMin = false;
            if (minDate instanceof Date && prevCalendarDay) {
              const pMid = new Date(
                prevCalendarDay.getFullYear(),
                prevCalendarDay.getMonth(),
                prevCalendarDay.getDate()
              );
              const minMid = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
              blockedByMin = pMid < minMid;
            }

            if (!blockedByMin) {
              const monthBefore = instance.currentMonth;
              const yearBefore = instance.currentYear;
              instance.changeMonth(-1);
              const moved =
                instance.currentMonth !== monthBefore || instance.currentYear !== yearBefore;

              if (moved) {
                window.setTimeout(() => {
                  if (!instance.isOpen) return;
                  if (!focusLastDayOfVisibleMonth(instance, refreshTabStops)) {
                    focusFirstLogicalDay(instance.calendarContainer);
                  }
                }, 0);
              } else {
                const prevBtn = getPrevBtn();
                if (prevBtn && !prevBtn.classList.contains('flatpickr-disabled')) {
                  prevBtn.focus({ preventScroll: true });
                }
              }
              return;
            }

            const prevBtn = getPrevBtn();
            if (prevBtn && !prevBtn.classList.contains('flatpickr-disabled')) {
              prevBtn.focus({ preventScroll: true });
            }
            return;
          }

          focusAdjacentEnabledDay(dayCells, dayIndex, -1);
          return;
        }
        if (key === 'ArrowRight') {
          consumeCalendarArrowKeydown(e);

          if (isDayInLastRowLastColumn(dayEl) && typeof instance.changeMonth === 'function') {
            const nextCalendarDay = getCalendarDatePlusDays(dayEl, 1);
            const maxDate = instance.config?.maxDate;
            let blockedByMax = false;
            if (maxDate instanceof Date && nextCalendarDay) {
              const nMid = new Date(
                nextCalendarDay.getFullYear(),
                nextCalendarDay.getMonth(),
                nextCalendarDay.getDate()
              );
              const maxMid = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
              blockedByMax = nMid > maxMid;
            }

            if (!blockedByMax) {
              const monthBefore = instance.currentMonth;
              const yearBefore = instance.currentYear;
              instance.changeMonth(1);
              const moved =
                instance.currentMonth !== monthBefore || instance.currentYear !== yearBefore;

              if (moved) {
                window.setTimeout(() => {
                  if (!instance.isOpen) return;
                  if (!focusFirstDayOfVisibleMonth(instance, refreshTabStops)) {
                    focusFirstLogicalDay(instance.calendarContainer);
                  }
                }, 0);
              } else {
                const nextBtn = getNextBtn();
                if (nextBtn && !nextBtn.classList.contains('flatpickr-disabled')) {
                  nextBtn.focus({ preventScroll: true });
                }
              }
              return;
            }

            const nextBtn = getNextBtn();
            if (nextBtn && !nextBtn.classList.contains('flatpickr-disabled')) {
              nextBtn.focus({ preventScroll: true });
            }
            return;
          }

          focusAdjacentEnabledDay(dayCells, dayIndex, 1);
          return;
        }
        if (key === 'ArrowUp') {
          consumeCalendarArrowKeydown(e);

          const firstRow = isDayInFirstGridRow(dayEl);
          const onPresentMonth = isInstanceViewingPresentMonth(instance);

          if (firstRow && (isWarrantyReturnsPage || !onPresentMonth) && typeof instance.changeMonth === 'function') {
            const monthBefore = instance.currentMonth;
            const yearBefore = instance.currentYear;
            instance.changeMonth(-1);
            const moved =
              instance.currentMonth !== monthBefore || instance.currentYear !== yearBefore;

            if (moved) {
              window.setTimeout(() => {
                if (!instance.isOpen) return;
                if (!focusLastDayOfVisibleMonth(instance, refreshTabStops)) {
                  focusFirstLogicalDay(instance.calendarContainer);
                }
              }, 0);
            } else {
              const prevBtn = getPrevBtn();
              if (prevBtn && !prevBtn.classList.contains('flatpickr-disabled')) {
                prevBtn.focus({ preventScroll: true });
              }
            }
            return;
          }

          const result = focusVerticalDay(dayCells, dayEl, -1);
          if (result === 'aboveGrid') {
            getMonthDropdown()?.focus({ preventScroll: true });
          }
          return;
        }
        if (key === 'ArrowDown') {
          consumeCalendarArrowKeydown(e);
          const result = focusVerticalDay(dayCells, dayEl, 1);
          if (result === 'belowGrid') {
            const targetWeek = getCalendarDatePlusDays(dayEl, DAY_GRID_COLS);
            const maxDate = instance.config?.maxDate;
            let blockedByMax = false;
            if (maxDate instanceof Date && targetWeek) {
              const tMid = new Date(targetWeek.getFullYear(), targetWeek.getMonth(), targetWeek.getDate());
              const maxMid = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
              blockedByMax = tMid > maxMid;
            }

            if (
              typeof instance.changeMonth === 'function' &&
              targetWeek &&
              !blockedByMax
            ) {
              const monthBefore = instance.currentMonth;
              const yearBefore = instance.currentYear;
              instance.changeMonth(1);
              const moved =
                instance.currentMonth !== monthBefore || instance.currentYear !== yearBefore;

              if (moved) {
                window.setTimeout(() => {
                  if (!instance.isOpen) return;
                  if (!focusFirstDayOfVisibleMonth(instance, refreshTabStops)) {
                    focusFirstLogicalDay(instance.calendarContainer);
                  }
                }, 0);
              } else {
                const nextBtn = getNextBtn();
                if (nextBtn && !nextBtn.classList.contains('flatpickr-disabled')) {
                  nextBtn.focus({ preventScroll: true });
                }
              }
            } else {
              const nextBtn = getNextBtn();
              if (nextBtn && !nextBtn.classList.contains('flatpickr-disabled')) {
                nextBtn.focus({ preventScroll: true });
              }
            }
          }
        }
      },
      true
    );

    instance.calendarContainer?.addEventListener(
      'keydown',
      (e) => {
        const el = document.activeElement;
        if (!el) return;

        const isPrev = el.classList?.contains('flatpickr-prev-month');
        const isNext = el.classList?.contains('flatpickr-next-month');

        if (
          (isPrev || isNext) &&
          !el.classList.contains('flatpickr-disabled') &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          el.click();
        }
      },
      true
    );

    instance.config.onMonthChange = [
      ...(instance.config.onMonthChange || []),
      refreshTabStops,
      refreshCalendarAriaPresentation,
    ];
    instance.config.onYearChange = [
      ...(instance.config.onYearChange || []),
      refreshTabStops,
      refreshCalendarAriaPresentation,
    ];
  }

  function initDateInputs(container) {
    if (!container || typeof flatpickr === 'undefined') return;

    container.querySelectorAll(selector).forEach((input) => {
      if (/** @type {any} */ (input)._flatpickr) return;
      flatpickr(input, getFlatpickrConfig(input));
    });
  }

  window.initDateInputs = initDateInputs;
  window.enableFlatpickrYearInput = enableFlatpickrYearInput;
  window.applyFlatpickrCalendarControlAriaLabels = applyCalendarControlAriaLabels;

  function initAll() {
    initDateInputs(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
